import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Gift, Users, DollarSign, Settings, LogOut, Plus, ExternalLink, Edit, Eye, Trash2, PartyPopper, Building2, MessageSquare, UserCheck, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { getApiUrl } from "../utils/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const API = getApiUrl();
      try {
        // Fetch user
        const userRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }

        // Fetch all sites
        const sitesRes = await fetch(`${API}/sites/my/all`, { credentials: 'include' });
        if (sitesRes.ok) {
          const sitesData = await sitesRes.json();
          setSites(sitesData);
          if (sitesData.length > 0) {
            setSelectedSite(sitesData[0]);
          }
        }

        // Fetch stats
        const statsRes = await fetch(`${API}/dashboard/stats`, { credentials: 'include' });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      const API = getApiUrl();
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePublish = async () => {
    if (!selectedSite) return;
    try {
      const API = getApiUrl();
      const res = await fetch(`${API}/sites/${selectedSite.site_id}/publish`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setSelectedSite({ ...selectedSite, is_published: true });
        setSites(prev => prev.map(s => s.site_id === selectedSite.site_id ? { ...s, is_published: true } : s));
        toast.success("Evento publicado com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao publicar evento");
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm("Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita.")) return;
    
    try {
      const API = getApiUrl();
      const res = await fetch(`${API}/sites/${siteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setSites(prev => prev.filter(s => s.site_id !== siteId));
        if (selectedSite?.site_id === siteId) {
          setSelectedSite(sites.find(s => s.site_id !== siteId) || null);
        }
        toast.success("Evento excluído!");
      }
    } catch (error) {
      toast.error("Erro ao excluir evento");
    }
  };

  const handleSelectSite = (site) => {
    setSelectedSite(site);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PartyPopper className="w-8 h-8 text-[#B3121B]" />
            <span className="font-cursive text-2xl text-[#6E0F1A]">Fiesta Lab</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#6E0F1A]/70 hidden sm:block">Olá, {user?.name?.split(' ')[0]}</span>
            {user?.is_admin && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="text-[#B3121B] border-[#B3121B]"
              >
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-[#6E0F1A]/70 hover:text-[#B3121B]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#6E0F1A] mb-2">
              Meus Eventos
            </h1>
            <p className="text-[#6E0F1A]/70">
              Gerencie seus eventos e acompanhe todas as métricas.
            </p>
          </div>
          <Button
            onClick={() => navigate('/onboarding')}
            className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full"
            data-testid="create-new-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Criar Novo Evento
          </Button>
        </div>

        {/* No Sites - Create CTA */}
        {sites.length === 0 && (
          <Card className="card-dashboard mb-8 border-2 border-dashed border-[#F3C9CF] bg-[#F3C9CF]/10">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-6">
                <Plus className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Crie seu primeiro evento
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6 max-w-md mx-auto">
                Comece agora mesmo a criar o site perfeito para compartilhar com seus convidados.
              </p>
              <Button
                onClick={() => navigate('/onboarding')}
                className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6 text-lg"
                data-testid="create-site-btn"
              >
                Criar Meu Evento
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Sites List */}
        {sites.length > 0 && (
          <>
            {/* Events Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {sites.map((site) => (
                <Card 
                  key={site.site_id}
                  className={`card-dashboard cursor-pointer transition-all ${
                    selectedSite?.site_id === site.site_id 
                      ? 'ring-2 ring-[#B3121B] border-[#B3121B]' 
                      : 'hover:border-[#B3121B]/30'
                  }`}
                  onClick={() => handleSelectSite(site)}
                  data-testid={`event-card-${site.site_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-cursive text-2xl text-[#B3121B]">
                          {site.partner1_name}{site.partner2_name ? ` & ${site.partner2_name}` : ''}
                        </h3>
                        <p className="text-sm text-[#6E0F1A]/60">/{site.slug}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.site_id); }}
                        className="text-[#6E0F1A]/40 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      {site.is_published ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-[#567D50]/10 text-[#567D50] px-2 py-1 rounded-full">
                          <span className="w-1.5 h-1.5 bg-[#567D50] rounded-full"></span>
                          Publicado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                          <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                          Rascunho
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Selected Site Details */}
            {selectedSite && (
              <>
                {/* Site Actions */}
                <Card className="card-dashboard mb-8">
                  <CardContent className="py-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h2 className="font-cursive text-3xl text-[#B3121B]">
                          {selectedSite.partner1_name}{selectedSite.partner2_name ? ` & ${selectedSite.partner2_name}` : ''}
                        </h2>
                        <p className="text-[#6E0F1A]/70 mt-1">
                          {selectedSite.is_published ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 bg-[#567D50] rounded-full"></span>
                              Publicado em: fiestalab.com.br/{selectedSite.slug}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                              Rascunho - não publicado
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {selectedSite.is_published && (
                          <Button
                            variant="outline"
                            onClick={() => window.open(`/casamento/${selectedSite.slug}`, '_blank')}
                            className="border-[#B3121B] text-[#B3121B] rounded-full"
                            data-testid="view-site-btn"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Site
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => navigate('/editor')}
                          className="border-[#6E0F1A] text-[#6E0F1A] rounded-full"
                          data-testid="edit-site-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                        {!selectedSite.is_published && (
                          <Button
                            onClick={handlePublish}
                            className="bg-[#567D50] hover:bg-[#4A6B45] text-white rounded-full"
                            data-testid="publish-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Publicar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <Card className="card-dashboard" data-testid="stat-raised">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total Arrecadado</p>
                          <p className="text-3xl font-semibold text-[#567D50] mt-1">
                            {formatCurrency(stats?.total_raised)}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-[#567D50]/10 rounded-full flex items-center justify-center">
                          <DollarSign className="w-6 h-6 text-[#567D50]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-dashboard" data-testid="stat-guests">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Convidados Confirmados</p>
                          <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                            {stats?.total_confirmed_guests || 0}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-[#6E0F1A]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-dashboard" data-testid="stat-rsvps">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total de RSVPs</p>
                          <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                            {stats?.total_rsvps || 0}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                          <Heart className="w-6 h-6 text-[#B3121B]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-dashboard" data-testid="stat-gifts">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Presentes</p>
                          <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                            {stats?.gifts_summary?.fulfilled || 0}/{stats?.gifts_summary?.total || 0}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                          <Gift className="w-6 h-6 text-[#B3121B]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/gifts')}
                    data-testid="manage-gifts-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Gift className="w-8 h-8 text-[#B3121B]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Gerenciar Presentes</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Adicione e edite sua lista de presentes</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/rsvps')}
                    data-testid="manage-rsvps-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-[#6E0F1A]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Ver Confirmações</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Gerencie os RSVPs dos convidados</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/editor')}
                    data-testid="edit-site-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#567D50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Settings className="w-8 h-8 text-[#567D50]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Personalizar Site</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Edite o conteúdo e visual do site</p>
                    </CardContent>
                  </Card>
                </div>

                {/* More Actions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/event-info')}
                    data-testid="event-info-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#B3121B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Info className="w-8 h-8 text-[#B3121B]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Informações</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Horários, traje e local</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/guest-list')}
                    data-testid="guest-list-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <UserCheck className="w-8 h-8 text-[#B3121B]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Lista de Convidados</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Importe e gerencie convidados</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/messages')}
                    data-testid="messages-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-[#6E0F1A]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Mural de Recados</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Veja as mensagens dos convidados</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="card-dashboard cursor-pointer hover:border-[#B3121B]/30"
                    onClick={() => navigate('/bank-settings')}
                    data-testid="bank-settings-card"
                  >
                    <CardContent className="py-8 text-center">
                      <div className="w-16 h-16 bg-[#567D50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-8 h-8 text-[#567D50]" />
                      </div>
                      <h3 className="font-serif text-xl text-[#6E0F1A] mb-2">Dados Bancários</h3>
                      <p className="text-sm text-[#6E0F1A]/60">Configure sua conta para recebimento</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Transactions */}
                {stats?.recent_transactions?.length > 0 && (
                  <Card className="card-dashboard">
                    <CardHeader>
                      <CardTitle className="font-serif text-xl text-[#6E0F1A]">
                        Últimas Transações
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {stats.recent_transactions.slice(0, 5).map((txn, index) => (
                          <div
                            key={txn.transaction_id || index}
                            className="flex items-center justify-between py-3 border-b border-[#F3C9CF]/30 last:border-0"
                          >
                            <div>
                              <p className="font-medium text-[#6E0F1A]">{txn.payer_name || 'Anônimo'}</p>
                              <p className="text-sm text-[#6E0F1A]/60">{txn.message || 'Sem mensagem'}</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-semibold ${txn.payment_status === 'paid' ? 'text-[#567D50]' : 'text-yellow-600'}`}>
                                {formatCurrency(txn.beneficiary_amount)}
                              </p>
                              <p className="text-xs text-[#6E0F1A]/50">
                                {txn.payment_status === 'paid' ? 'Confirmado' : 'Pendente'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
